import { WorkflowRun } from "@/hooks/useRuns";
import {
  Activity,
  Calendar,
  GitCommit,
  GitPullRequest,
  Play,
} from "lucide-react";
import Link from "next/link";
import React from "react";

export type JobType = "matrix" | "reusable" | "simple";

/** `"pull_request"` → `"Pull Request"` */
export const formatEventName = (event: string | undefined): string => {
  if (!event) return "";
  return event
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const getEventIcon = (
  event: string | undefined,
  className: string = "w-3.5 h-3.5 mr-1.5",
) => {
  switch (event) {
    case "push":
      return <GitCommit className={className} />;
    case "pull_request":
      return <GitPullRequest className={className} />;
    case "workflow_dispatch":
      return <Play className={className} />;
    case "schedule":
      return <Calendar className={className} />;
    default:
      return <Activity className={className} />;
  }
};

/** Event badge for a run; pull-request events link out to the PR. */
export const getEventComponent = (run: WorkflowRun): React.ReactNode => {
  if (run.event === "pull_request" && run.pull_request) {
    const eventName = `#${run.pull_request.number}`;
    return (
      <Link
        href={run.pull_request.html_url}
        target="_blank"
        rel="noopener noreferrer"
        className="truncate flex items-center hover:underline"
        title={eventName}
      >
        {getEventIcon(run.event)}
        <span className="xl:inline hidden">{eventName}</span>
      </Link>
    );
  }
  if (!run.event) {
    return null;
  }
  return (
    <span
      className="truncate flex items-center"
      title={formatEventName(run.event)}
    >
      {getEventIcon(run.event)}
      <span className="xl:inline hidden">{formatEventName(run.event)}</span>
    </span>
  );
};

/** `"deploy / setup"` → `"reusable"`, `"test (1, 2)"` → `"matrix"`, `"build"` → `"simple"` */
export const identifyJobType = (jobName: string): JobType => {
  if (jobName.match(/[^(]*[^)]*\s\/\s/)) {
    return "reusable";
  }
  if (jobName.match(/[^/]*\s\([^)]*\)/)) {
    return "matrix";
  }
  return "simple";
};

/**
 * Strips matrix parameters and reusable-workflow suffixes from a job name.
 * Whether the resulting names should be grouped is the caller's call.
 *
 * - `"test (1, 2)"` → `"test"`
 * - `"deploy / setup"` → `"deploy"`
 * - `"integration (nodejs) / run-tests"` → `"integration"`
 */
export const getJobGroupName = (jobName: string): string => {
  const nameWithoutMatrixArgs = jobName.replace(/\s*\([^)]*\)/g, "");
  if (nameWithoutMatrixArgs.match(/\s\/\s/)) {
    return nameWithoutMatrixArgs.split(" / ")[0].trim();
  }

  // Return the cleaned name
  return nameWithoutMatrixArgs.trim();
};
